import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { SelectButton } from 'primereact/selectbutton';
import { Sidebar } from 'primereact/sidebar';
import { Toast } from 'primereact/toast';
import { useAppContext } from '../Context';
import { ApiEndpoints } from '../constants/appConstants';
import httpService from '../services/httpService';
import { IntakeQuestion, IntakeSurvey, QuestionMapping as Mapping, TargetQuestion,
    questionAttribute, questionMappingService } from '../services/questionMappingService';
import './QuestionMapping.scss';
import ComponentHeader from './ComponentHeader';
import { buildMappingConditionalDisplay } from '../utils/mappingConditionalDisplay';

type MappingStatus = 'Mapped' | 'Unmapped';
type MappingView = 'Mapped' | 'Unmapped Intake Questions' | 'Unmapped Scoring Question';
interface QuestionRow { question: IntakeQuestion; mapping?: Mapping; status: MappingStatus }
interface ReviewYear { reviewYear: number }

const apiError = (error: unknown, fallback: string) => axios.isAxiosError<{ message?: string }>(error)
    ? error.response?.data?.message || fallback : fallback;
const sameValue = (left: string | null | undefined, right: string | null | undefined) =>
    (left ?? '').trim().toLowerCase() === (right ?? '').trim().toLowerCase();

const items = [{ label: 'Question Mapping', path: '/question-mapping' }];

export default function QuestionMapping() {
    const { providerTypes, providerType: initialProvider, reviewYear: initialYear } = useAppContext();
    const [providerType, setProviderType] = useState(initialProvider);
    const [reviewYear, setReviewYear] = useState<number | null>(null);
    const [years, setYears] = useState<number[]>([]);
    const [surveys, setSurveys] = useState<IntakeSurvey[]>([]);
    const [surveyId, setSurveyId] = useState<string | null>(null);
    const [questions, setQuestions] = useState<IntakeQuestion[]>([]);
        const [unmappedScoringQuestions, setUnmappedScoringQuestions] = useState<TargetQuestion[]>([]);
    const [mappings, setMappings] = useState<Mapping[]>([]);
    const [loadingContext, setLoadingContext] = useState(false);
    const [loadingSurveys, setLoadingSurveys] = useState(false);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [error, setError] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<MappingView>('Unmapped Intake Questions');
    const [first, setFirst] = useState(0);
    const [editorOpen, setEditorOpen] = useState(false);
        const [editorMode, setEditorMode] = useState<'intake' | 'scoring'>('intake');
        const [reverseTargetGuid, setReverseTargetGuid] = useState<string | null>(null);
        const [loadingUnmappedScoring, setLoadingUnmappedScoring] = useState(false);
    const [sourceGuid, setSourceGuid] = useState<string | null>(null);
    const [sourceMeasureSearch, setSourceMeasureSearch] = useState('');
    const [sourceTierSearch, setSourceTierSearch] = useState('');
    const [measure, setMeasure] = useState('');
    const [tier, setTier] = useState('');
    const [targets, setTargets] = useState<TargetQuestion[]>([]);
    const [targetGuid, setTargetGuid] = useState<string | null>(null);
    const [targetSearch, setTargetSearch] = useState('');
    const [loadingTargets, setLoadingTargets] = useState(false);
    const [targetError, setTargetError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);
    const [mappingToDeactivate, setMappingToDeactivate] = useState<QuestionRow | null>(null);
    const [deactivating, setDeactivating] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);
    const [dirty, setDirty] = useState(false);
    const toast = useRef<Toast>(null);
    const mounted = useRef(true);
    const preferredYear = useRef(initialYear);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setReviewYear(null);
        setYears([]);
        setSurveyId(null);
        setSurveys([]);
        setQuestions([]);
        setMappings([]);
        setError('');
        if (!providerType) return () => controller.abort();
        setLoadingContext(true);
        httpService.get<ReviewYear[]>(`${ApiEndpoints.GetAllActiveReviewYearDetails}/${encodeURIComponent(providerType)}`,
            undefined, { signal: controller.signal })
            .then(response => {
                if (controller.signal.aborted) return;
                const available = [...new Set(response.data.map(item => item.reviewYear))].filter(Number.isInteger).sort((left, right) => right - left);
                setYears(available);
                setReviewYear(preferredYear.current && available.includes(preferredYear.current) ? preferredYear.current : available[0] ?? null);
            })
            .catch(error => { if (!controller.signal.aborted) setError(apiError(error, 'Unable to load review years.')); })
            .finally(() => { if (!controller.signal.aborted) setLoadingContext(false); });
        return () => controller.abort();
    }, [providerType, refresh]);

    useEffect(() => {
        const controller = new AbortController();
        setSurveyId(null);
        setSurveys([]);
        setQuestions([]);
        setMappings([]);
        setError('');
        if (!providerType || !reviewYear) {
            setLoadingSurveys(false);
            return () => controller.abort();
        }
        setLoadingSurveys(true);
        questionMappingService.surveys(reviewYear, providerType, controller.signal)
            .then(data => { if (!controller.signal.aborted) setSurveys(data); })
            .catch(error => { if (!controller.signal.aborted) setError(apiError(error, 'Unable to load Intake Portal surveys.')); })
            .finally(() => { if (!controller.signal.aborted) setLoadingSurveys(false); });
        return () => controller.abort();
    }, [providerType, reviewYear]);

    useEffect(() => {
        const controller = new AbortController();
        setQuestions([]);
        setMappings([]);
        setFirst(0);
        if (!surveyId) {
            setLoadingQuestions(false);
            return () => controller.abort();
        }
        setError('');
        setLoadingQuestions(true);
        Promise.all([questionMappingService.intakeQuestions(surveyId, controller.signal), questionMappingService.mappings(controller.signal)])
            .then(([intake, saved]) => {
                if (controller.signal.aborted) return;
                setQuestions([...intake]);
                setMappings(saved);
            })
            .catch(error => { if (!controller.signal.aborted) setError(apiError(error, 'Unable to load questions and mappings.')); })
            .finally(() => { if (!controller.signal.aborted) setLoadingQuestions(false); });
        return () => controller.abort();
    }, [surveyId]);

    useEffect(() => {
        const controller = new AbortController();
        setUnmappedScoringQuestions([]);
        if (!providerType || !reviewYear || !surveyId) {
            setLoadingUnmappedScoring(false);
            return () => controller.abort();
        }

        setLoadingUnmappedScoring(true);
        questionMappingService.unmappedTargets(reviewYear, providerType, controller.signal)
            .then(data => {
                if (!controller.signal.aborted) setUnmappedScoringQuestions(data);
            })
            .catch(error => {
                if (!controller.signal.aborted) setError(apiError(error, 'Unable to load unmapped scoring questions.'));
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoadingUnmappedScoring(false);
            });

        return () => controller.abort();
    }, [providerType, reviewYear, surveyId, mappings]);

    useEffect(() => {
        const controller = new AbortController();
        setTargets([]);
        setTargetGuid(null);
        setTargetError('');
        if (!editorOpen || editorMode === 'scoring' || !measure.trim() || !tier.trim() || !reviewYear) {
            setLoadingTargets(false);
            return () => controller.abort();
        }
        setLoadingTargets(true);
        const timer = setTimeout(() => {
            questionMappingService.targets({ reviewYear, providerType, measureNumber: measure.trim(), tier: tier.trim() }, controller.signal)
                .then(data => {
                    if (controller.signal.aborted) return;
                    setTargets(data);
                    const current = mappings.find(item => sameValue(item.intakeQuestionGuid, sourceGuid));
                    if (current && data.some(item => sameValue(item.questionGuid, current.scoringQuestionGuid))) setTargetGuid(current.scoringQuestionGuid);
                })
                .catch(error => { if (!controller.signal.aborted) setTargetError(apiError(error, 'Unable to load active scoring questions.')); })
                .finally(() => { if (!controller.signal.aborted) setLoadingTargets(false); });
        }, 300);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [editorOpen, editorMode, sourceGuid, measure, tier, providerType, reviewYear, mappings]);

    const rows: QuestionRow[] = questions.map(question => {
        const mapping = mappings.find(item => sameValue(item.intakeQuestionGuid, question.questionGuid));
        return { question, mapping, status: mapping ? 'Mapped' : 'Unmapped' };
    });
    const filteredRows = rows.filter(row => ((status === 'Mapped' && row.status === 'Mapped') ||
        (status === 'Unmapped Intake Questions' && row.status === 'Unmapped')) &&
        [row.question.questionText, row.question.questionGuid, questionAttribute(row.question, 'measure_number'),
            questionAttribute(row.question, 'tier'), row.mapping?.scoringQuestionGuid, row.mapping?.targetQuestion?.questionText]
            .some(value => value?.toLowerCase().includes(search.trim().toLowerCase())));
    const filteredUnmappedScoringQuestions = unmappedScoringQuestions.filter(question =>
        [question.questionText, question.questionGuid, question.measureNumber, question.tierIdentifier]
            .some(value => value?.toLowerCase().includes(search.trim().toLowerCase())));
    const unmappedIntakeQuestions = questions.filter(question =>
        !mappings.some(mapping => sameValue(mapping.intakeQuestionGuid, question.questionGuid)));
    const source = questions.find(item => sameValue(item.questionGuid, sourceGuid));
    const parentQuestion = source?.parentQuestionGuid
        ? questions.find(item => sameValue(item.questionGuid, source.parentQuestionGuid))
        : undefined;
    const parentMappingMissing = !!source?.parentQuestionGuid &&
        !mappings.some(mapping => sameValue(mapping.intakeQuestionGuid, source.parentQuestionGuid));
    const currentMapping = mappings.find(item => sameValue(item.intakeQuestionGuid, sourceGuid));
    const sourceQuestionOptions = (editorMode === 'scoring' || !currentMapping ? unmappedIntakeQuestions : questions)
        .filter(question => questionAttribute(question, 'measure_number').toLowerCase().includes(sourceMeasureSearch.trim().toLowerCase()) &&
            questionAttribute(question, 'tier').toLowerCase().includes(sourceTierSearch.trim().toLowerCase()));
    const target = editorMode === 'scoring'
        ? unmappedScoringQuestions.find(item => sameValue(item.questionGuid, reverseTargetGuid))
        : targets.find(item => sameValue(item.questionGuid, targetGuid));
    const survey = surveys.find(item => item.surveyId === surveyId);
    const busy = loadingContext || loadingSurveys || loadingQuestions || loadingUnmappedScoring;
    const canSave = source && target?.questionGuid && surveyId && reviewYear && measure.trim() && tier.trim() &&
        !saving && !parentMappingMissing && (editorMode === 'scoring'
            ? sameValue(target.measureNumber, measure) && sameValue(target.tierIdentifier, tier)
            : !loadingTargets && !sameValue(target.questionGuid, currentMapping?.scoringQuestionGuid));

    const selectSource = (guid: string) => {
        const question = questions.find(item => sameValue(item.questionGuid, guid));
        if (!question) return;
        setSourceGuid(guid);
        const scoringQuestion = editorMode === 'scoring'
            ? unmappedScoringQuestions.find(item => sameValue(item.questionGuid, reverseTargetGuid))
            : undefined;
        setMeasure(editorMode === 'scoring' ? scoringQuestion?.measureNumber || '' : questionAttribute(question, 'measure_number'));
        setTier(editorMode === 'scoring' ? scoringQuestion?.tierIdentifier || '' : questionAttribute(question, 'tier'));
        setTargetGuid(null);
        setTargets([]);
        setTargetSearch('');
        setSaveError('');
    };

    const openEditor = (question?: IntakeQuestion) => {
        setEditorMode('intake');
        setReverseTargetGuid(null);
        setSourceMeasureSearch('');
        setSourceTierSearch('');
        selectSource((question ?? unmappedIntakeQuestions[0])?.questionGuid ?? '');
        setDirty(false);
        setEditorOpen(true);
    };

    const openScoringEditor = (question: TargetQuestion) => {
        setEditorMode('scoring');
        setReverseTargetGuid(question.questionGuid);
        setSourceGuid(null);
        setSourceMeasureSearch(question.measureNumber ?? '');
        setSourceTierSearch(question.tierIdentifier ?? '');
        setMeasure(question.measureNumber ?? '');
        setTier(question.tierIdentifier ?? '');
        setTargetGuid(question.questionGuid);
        setTargets([]);
        setTargetSearch('');
        setSaveError('');
        setDirty(false);
        setEditorOpen(true);
    };

    const closeEditor = () => {
        if (saving) return;
        if (dirty) setConfirmClose(true);
        else setEditorOpen(false);
    };

    const save = async () => {
        if (!canSave || !source || !target?.questionGuid || !surveyId || !reviewYear) return;
        setSaving(true);
        setSaveError('');
        try {
            const conditionalDisplay = buildMappingConditionalDisplay(source, questions, mappings);
            const activeMappingId = editorMode === 'intake' ? currentMapping?.questionMappingId : undefined;
            const saved = await questionMappingService.save({
                intakeQuestionGuid: source.questionGuid, scoringQuestionGuid: target.questionGuid,
                surveyId, reviewYear, providerType, measureNumber: measure.trim(), tier: tier.trim(),
                parentQuestionGuid: source.parentQuestionGuid ?? null,
                conditionalDisplay,
                ...(activeMappingId !== undefined && currentMapping
                    ? { expectedUpdatedAt: currentMapping.updatedAt }
                    : {})
            }, activeMappingId);
            if (!mounted.current) return;
            setMappings(previous => [...previous.filter(item => item.questionMappingId !== saved.questionMappingId), saved]);
            setDirty(false);
            setEditorOpen(false);
            toast.current?.show({ severity: 'success', summary: 'Mapping saved', detail: 'The question link has been updated.', life: 3500 });
        } catch (error) {
            if (!mounted.current) return;
            setSaveError(apiError(error, error instanceof Error && !axios.isAxiosError(error)
                ? error.message : 'Unable to save mapping. Please try again.'));
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                try {
                    const latest = await questionMappingService.mappings();
                    if (mounted.current) setMappings(latest);
                } catch {
                    if (mounted.current) setSaveError('Mapping conflict. Close the editor and refresh before trying again.');
                }
            }
        } finally { if (mounted.current) setSaving(false); }
    };

    const deactivateMapping = async () => {
        const mapping = mappingToDeactivate?.mapping;
        if (!mapping || deactivating) return;
        setDeactivating(true);
        try {
            await questionMappingService.deactivate(mapping.questionMappingId, mapping.updatedAt);
            setMappings(previous => previous.filter(item => item.questionMappingId !== mapping.questionMappingId));
            setMappingToDeactivate(null);
            toast.current?.show({
                severity: 'success',
                summary: 'Mapping deactivated',
                detail: 'The Intake and scoring questions are available to remap.',
                life: 4000
            });
        } catch (error) {
            const message = apiError(error, 'Unable to deactivate this mapping. Please refresh and try again.');
            toast.current?.show({ severity: 'error', summary: 'Deactivation failed', detail: message, life: 5000 });
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                try {
                    setMappings(await questionMappingService.mappings());
                } catch {
                    setError('The mapping changed. Refresh the page before trying again.');
                }
            }
        } finally {
            setDeactivating(false);
        }
    };

    const sourceBody = ({ question }: QuestionRow) => <div className="qm-question">
        <div className="qm-tags"><span className="qm-measure">{questionAttribute(question, 'measure_number') || `Question ${question.sequenceNumber ?? question.questionId}`}</span>
            <span>{questionAttribute(question, 'tier') || 'No tier attribute'}</span></div>
        <div className="qm-question-text">{question.questionText || 'Untitled question'}</div>
        <span className="qm-meta">{question.dataType || question.inputType || 'Unknown type'}{question.isQuestionRequired ? ' / Required' : ''}</span>
    </div>;
    const targetBody = ({ mapping }: QuestionRow) => mapping?.targetQuestion ? <div className="qm-question">
        <div className="qm-tags"><span className="qm-measure qm-target-measure">{mapping.targetQuestion.measureNumber || 'No measure'}</span><span>{mapping.targetQuestion.tierIdentifier || 'No tier identifier'}</span></div>
        <div className="qm-question-text">{mapping.targetQuestion.questionText || 'Untitled question'}</div>
        <span className="qm-meta">{mapping.targetQuestion.dataFieldType || 'Unknown type'}</span>
    </div> : <div className="qm-empty-target">{mapping ? 'Linked question is no longer active' : 'No target question linked'}
        <div className="qm-meta">{mapping ? mapping.scoringQuestionGuid : 'Awaiting mapping'}</div></div>;

    return <div style={{ marginTop: '42px', marginBottom: '50px', marginLeft: '5px' }}>
          <ComponentHeader hideProviderTypeDropdown={true} breadcrumbs={items}/>
        <div className="question-mapping">
        <Toast ref={toast} />
        <div className="qm-heading"><div><h1>Question Mapping</h1><p>Intake Portal <i className="pi pi-arrow-right" aria-hidden="true" /> PBC Scoring Portal</p></div>
            <Button label="Add Mapping" icon="pi pi-plus" disabled={busy || !!error || !unmappedIntakeQuestions.length} onClick={() => openEditor()} /></div>
        <div className="qm-filters">
            <div><label htmlFor="qm-provider">Provider Type</label><Dropdown inputId="qm-provider" value={providerType} options={providerTypes}
                optionLabel="providerType" optionValue="providerCode" placeholder="Select provider type" onChange={event => { setProviderType(event.value); setReviewYear(null); setSurveyId(null); }} /></div>
            <div><label htmlFor="qm-year">Review Year</label><Dropdown inputId="qm-year" value={reviewYear} options={years} placeholder="Select year"
                loading={loadingContext} disabled={loadingContext || !years.length} onChange={event => { setReviewYear(event.value); setSurveyId(null); }} /></div>
            <div className="qm-survey-filter"><label htmlFor="qm-survey"><span className="qm-dot" /> Intake Portal Survey</label>
                <Dropdown inputId="qm-survey" value={surveyId} options={surveys} optionLabel="surveyName" optionValue="surveyId" filter
                    placeholder={loadingSurveys ? 'Loading surveys...' : 'Select a survey'} loading={loadingSurveys} disabled={!reviewYear || loadingSurveys}
                    onChange={event => setSurveyId(event.value)} /></div>
            <Button icon="pi pi-refresh" outlined aria-label="Refresh question mappings" title="Refresh question mappings" disabled={busy}
                onClick={() => setRefresh(value => value + 1)} />
        </div>
        {error && <div className="qm-alert" role="alert"><Message severity="error" text={error} /></div>}
        {!busy && !error && !years.length && <div className="qm-alert"><Message severity="info" text="No active review years are available for this provider type." /></div>}
        {!busy && !error && reviewYear && !surveys.length && <div className="qm-alert"><Message severity="info" text="No Intake Portal surveys are available for this provider type and review year." /></div>}
        <div className="qm-stats" aria-live="polite"><span><strong>{rows.length}</strong> Intake questions</span>
            <span><strong className="qm-green">{rows.filter(row => row.status === 'Mapped').length}</strong> Mapped</span>
            <span><strong>{rows.filter(row => row.status === 'Unmapped').length}</strong> Unmapped Intake</span>
            <span><strong>{unmappedScoringQuestions.length}</strong> Unmapped Scoring</span></div>
        <div className="qm-toolbar"><span className="p-input-icon-left"><i className="pi pi-search" aria-hidden="true" /><InputText value={search}
            aria-label="Search question, measure, tier, or GUID" placeholder="Search question, measure, tier, or GUID" onChange={event => { setSearch(event.target.value); setFirst(0); }} /></span>
            <SelectButton value={status} options={['Mapped', 'Unmapped Intake Questions', 'Unmapped Scoring Question']} allowEmpty={false}
                onChange={event => { setStatus(event.value); setFirst(0); }} /></div>
        {status === 'Unmapped Scoring Question' ? <DataTable value={filteredUnmappedScoringQuestions} dataKey="questionGuid"
            loading={loadingUnmappedScoring} paginator rows={10} first={first} onPage={event => setFirst(event.first)}
            paginatorTemplate="CurrentPageReport PrevPageLink PageLinks NextPageLink"
            currentPageReportTemplate="Showing {first} - {last} of {totalRecords} questions" tableStyle={{ minWidth: '760px' }}
            emptyMessage={!surveyId ? 'Select an Intake Portal survey to view mapping candidates.' : 'No unmapped scoring questions found for this provider type and review year.'}>
            <Column header="SOURCE QUESTION / INTAKE" body={() => <div className="qm-empty-target">No Intake question linked<div className="qm-meta">Select a source question to map</div></div>} style={{ width: '38%' }} />
            <Column header="TARGET QUESTION / PBC" body={(question: TargetQuestion) => <div className="qm-question">
                <div className="qm-tags"><span className="qm-measure qm-target-measure">{question.measureNumber || 'No measure'}</span><span>{question.tierIdentifier || 'No tier identifier'}</span></div>
                <div className="qm-question-text">{question.questionText || 'Untitled question'}</div><span className="qm-meta">{question.dataFieldType || 'Unknown type'}</span>
            </div>} style={{ width: '38%' }} />
            <Column header="STATUS" body={() => <span className="qm-status qm-status-unmapped"><i className="pi pi-minus" aria-hidden="true" />Unmapped</span>} />
            <Column body={(question: TargetQuestion) => <Button icon="pi pi-link" outlined title="Map scoring question"
                aria-label={`Map scoring question ${question.questionText}`} onClick={() => openScoringEditor(question)} />} style={{ width: '64px' }} />
        </DataTable> : <DataTable value={filteredRows} dataKey="question.questionGuid" loading={loadingQuestions} paginator rows={10} first={first}
            onPage={event => setFirst(event.first)} paginatorTemplate="CurrentPageReport PrevPageLink PageLinks NextPageLink"
            currentPageReportTemplate="Showing {first} - {last} of {totalRecords} questions" tableStyle={{ minWidth: '760px' }}
            emptyMessage={error ? 'Questions could not be loaded.' : !surveyId ? 'Select an Intake Portal survey to view question mappings.' : 'No questions found.'}>
            <Column header="SOURCE QUESTION / INTAKE" body={sourceBody} style={{ width: '38%' }} />
            <Column header="TARGET QUESTION / PBC" body={targetBody} style={{ width: '38%' }} />
            <Column header="STATUS" body={(row: QuestionRow) => <span className={`qm-status qm-status-${row.status === 'Mapped' ? 'mapped' : 'unmapped'}`}>
                <i className={`pi ${row.status === 'Mapped' ? 'pi-check' : 'pi-minus'}`} aria-hidden="true" />{row.status}</span>} />
            <Column body={(row: QuestionRow) => row.mapping
                ? <Button icon="pi pi-ban" outlined severity="danger" title="Deactivate mapping"
                    aria-label={`Deactivate mapping for ${row.question.questionText}`} onClick={() => setMappingToDeactivate(row)} />
                : <Button icon="pi pi-link" outlined title="Map question" aria-label={`Map question ${row.question.questionText}`}
                    onClick={() => openEditor(row.question)} />} style={{ width: '64px' }} />
        </DataTable>}
        <Sidebar visible={editorOpen} onHide={closeEditor} position="right" className="qm-editor" blockScroll dismissable={!saving} closeOnEscape={!saving}
            header={<div><h2>{currentMapping ? 'Edit Mapping' : 'Add Mapping'}</h2><p>{survey?.surveyName}</p></div>}>
            <div className="qm-editor-context"><span>{providerType}</span><span>Review year {reviewYear}</span></div>
            <div className="qm-compare"><section><div className="qm-portal-title"><span className="qm-dot" /> SOURCE / INTAKE PORTAL</div>
                <label htmlFor="qm-source">Question</label>
                <div className="qm-target-filters qm-intake-filters">
                    <div><label htmlFor="qm-intake-measure-search">Search by measure number</label><InputText id="qm-intake-measure-search"
                        value={sourceMeasureSearch} placeholder="Any measure" disabled={saving}
                        onChange={event => setSourceMeasureSearch(event.target.value)} /></div>
                    <div><label htmlFor="qm-intake-tier-search">Search by tier</label><InputText id="qm-intake-tier-search"
                        value={sourceTierSearch} placeholder="Any tier" disabled={saving}
                        onChange={event => setSourceTierSearch(event.target.value)} /></div>
                </div>
                <Dropdown inputId="qm-source" value={sourceGuid}
                    appendTo="self" panelClassName="qm-source-question-panel"
                    options={sourceQuestionOptions
                        .map(item => ({ label: `${questionAttribute(item, 'measure_number') || item.sequenceNumber || item.questionId} / ${item.questionText || 'Untitled question'}`, value: item.questionGuid }))}
                    filter disabled={saving} onChange={event => { selectSource(event.value); setDirty(true); }}
                    placeholder={editorMode === 'scoring' ? 'Search an unmapped Intake question' : 'Select source question'} />
                {parentMappingMissing && <div className="qm-alert" role="alert"><Message severity="warn"
                    text={`Map the parent question first: ${parentQuestion?.questionText || source?.parentQuestionGuid}`} /></div>}
                {source && <div className="qm-detail"><h3>{source.questionText || 'Untitled question'}</h3><dl>
                    <div><dt>Measure number</dt><dd>{questionAttribute(source, 'measure_number') || 'Not provided'}</dd></div>
                    <div><dt>Tier</dt><dd>{questionAttribute(source, 'tier') || 'Not provided'}</dd></div>
                    <div><dt>Response type</dt><dd>{source.dataType || source.inputType || 'Not provided'}</dd></div>
                    <div><dt>Required</dt><dd>{source.isQuestionRequired ? 'Yes' : 'No'}</dd></div></dl>
                    <div className="qm-guid"><span>QUESTION GUID</span>{source.questionGuid}</div></div>}
            </section><section><div className="qm-portal-title"><span className="qm-dot qm-blue-dot" /> TARGET / PBC SCORING PORTAL</div>
                {editorMode === 'intake' && <><div className="qm-target-filters"><div><label htmlFor="qm-measure">Measure Number</label><InputText id="qm-measure" value={measure} maxLength={100} disabled={saving}
                    onChange={event => { setMeasure(event.target.value); setTargetGuid(null); setTargets([]); setDirty(true); }} /></div>
                    <div><label htmlFor="qm-tier">Tier Code</label><InputText id="qm-tier" value={tier} maxLength={50} disabled={saving}
                        onChange={event => { setTier(event.target.value); setTargetGuid(null); setTargets([]); setDirty(true); }} /></div></div>
                <label htmlFor="qm-target-search">Find Target Question</label><InputText id="qm-target-search" value={targetSearch} placeholder="Search question or GUID"
                    disabled={saving} onChange={event => setTargetSearch(event.target.value)} />
                {targetError && <div role="alert" className="qm-alert"><Message severity="error" text={targetError} /></div>}
                <div className="qm-target-list" aria-busy={loadingTargets}>
                    {loadingTargets ? <div className="qm-target-empty"><i className="pi pi-spin pi-spinner" /> Loading active questions...</div>
                        : !measure.trim() || !tier.trim() ? <div className="qm-target-empty">Measure number and tier code are required.</div>
                        : targets.filter(item => `${item.questionText ?? ''} ${item.questionGuid ?? ''} ${item.tierIdentifier ?? ''}`.toLowerCase().includes(targetSearch.toLowerCase())).length === 0
                            ? <div className="qm-target-empty">No matching active questions.</div>
                            : targets.filter(item => `${item.questionText ?? ''} ${item.questionGuid ?? ''} ${item.tierIdentifier ?? ''}`.toLowerCase().includes(targetSearch.toLowerCase())).map(item =>
                                <label key={item.questionGuid} className={`qm-target-option ${sameValue(targetGuid, item.questionGuid) ? 'qm-target-selected' : ''}`}>
                                    <input type="radio" name="qm-target" checked={sameValue(targetGuid, item.questionGuid)} disabled={saving || !item.questionGuid}
                                        onChange={() => { setTargetGuid(item.questionGuid); setDirty(true); setSaveError(''); }} />
                                    <span><span className="qm-measure qm-target-measure">{item.measureNumber}</span><span className="qm-meta">{item.tierIdentifier || 'No tier identifier'}</span>
                                        <strong>{item.questionText || 'Untitled question'}</strong><span className="qm-meta">{item.dataFieldType || 'Unknown type'}</span></span>
                                </label>)}
                </div></>}
                {target && <div className="qm-detail"><label>SELECTED TARGET</label><h3>{target.questionText}</h3>
                    <dl><div><dt>Measure number</dt><dd>{target.measureNumber}</dd></div><div><dt>Tier identifier</dt><dd>{target.tierIdentifier || 'Not provided'}</dd></div>
                        <div><dt>Response type</dt><dd>{target.dataFieldType || 'Not provided'}</dd></div><div><dt>Review year</dt><dd>{target.reviewYear}</dd></div></dl>
                    <div className="qm-guid"><span>QUESTION GUID</span>{target.questionGuid}</div></div>}
            </section></div>
            {currentMapping && <div className="qm-existing-link"><span>Current target GUID</span>{currentMapping.scoringQuestionGuid}</div>}
            {saveError && <div className="qm-alert" role="alert"><Message severity="error" text={saveError} /></div>}
            <div className="qm-editor-footer"><Button label="Cancel" outlined disabled={saving} onClick={closeEditor} />
                <Button label="Save Mapping" icon="pi pi-check" loading={saving} disabled={!canSave} onClick={save} /></div>
        </Sidebar>
        <Dialog header="Discard changes?" visible={confirmClose} onHide={() => setConfirmClose(false)} style={{ width: 'min(440px, 95vw)' }}
            footer={<><Button label="Keep Editing" outlined onClick={() => setConfirmClose(false)} />
                <Button label="Discard" severity="danger" onClick={() => { setConfirmClose(false); setEditorOpen(false); setDirty(false); }} /></>}>
            The unsaved mapping changes will be discarded.
        </Dialog>
        <Dialog header="Deactivate mapping?" visible={!!mappingToDeactivate} onHide={() => !deactivating && setMappingToDeactivate(null)}
            style={{ width: 'min(520px, 95vw)' }}
            footer={<><Button label="Cancel" outlined disabled={deactivating} onClick={() => setMappingToDeactivate(null)} />
                <Button label="Deactivate" icon="pi pi-ban" severity="danger" loading={deactivating} onClick={deactivateMapping} /></>}>
            {mappingToDeactivate && <div className="qm-deactivate-confirm">
                <p>This will deactivate the current mapping. Both questions will become available for a new mapping.</p>
                <div><span>Intake question</span><strong>{mappingToDeactivate.question.questionText || 'Untitled question'}</strong></div>
                <div><span>Scoring question</span><strong>{mappingToDeactivate.mapping?.targetQuestion?.questionText || mappingToDeactivate.mapping?.scoringQuestionGuid}</strong></div>
            </div>}
        </Dialog>
    </div>
    </div>;
}